import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Heading, Button, Badge, Spinner, Alert, AlertIcon, HStack, VStack,
  Text, Input, Select, Card, CardBody, IconButton, useToast, Flex, Tooltip, Checkbox,
  SimpleGrid, Link, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, Textarea, FormControl, FormLabel,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  FiArrowLeft, FiRefreshCw, FiSearch, FiTruck, FiExternalLink, FiRotateCcw, FiList,
} from "react-icons/fi";
import {
  getListasComprasKanban, marcarListaRetirada, desfazerListaRetirada,
  getConfiguracoesListaCompras,
} from "../services/api";
import ListaCompraDetalhes from "./ListaCompraDetalhes";
import ConfirmDialog from "../components/ConfirmDialog";
import ModalColetaLalamove from "../components/LalamoveColeta/ModalColetaLalamove";
import SeloCorrida from "../components/LalamoveColeta/SeloCorrida";

// A lista abre em modal, mas o botao continua sendo um link de verdade: Ctrl+clique
// e clique do meio abrem em outra aba, e la o "voltar" retorna pro quadro.
const linkDetalhes = (id) => `/listas-compras/${id}?origem=kanban`;

const ACCENT = "#F68633";
const TEAL = "#01695E";

// Colunas do quadro. Só "retirado" recebe drop: os outros status são calculados
// pelo sistema (resposta do fornecedor / aprovação), não se muda arrastando.
const COLUNAS = [
  { key: "pendente", label: "Pendente", cor: "gray", ajuda: "O fornecedor ainda não terminou de preencher." },
  { key: "respondido", label: "Respondido", cor: "yellow", ajuda: "Fornecedor respondeu tudo — aguardando sua aprovação." },
  { key: "aprovado", label: "Aprovado", cor: "green", ajuda: "Compra aprovada — aguardando a retirada no fornecedor." },
  { key: "retirado", label: "Retirado", cor: "purple", ajuda: "Mercadoria já retirada, com o link de entrega." },
];
const VAZIO = { pendente: [], respondido: [], aprovado: [], retirado: [] };

const PERIODOS = [7, 15, 30, 60, 90, 180, 365];
const PARADO_OPCOES = [
  { valor: 0, label: "Qualquer" },
  { valor: 2, label: "+ de 2 dias" },
  { valor: 3, label: "+ de 3 dias" },
  { valor: 5, label: "+ de 5 dias" },
  { valor: 7, label: "+ de 7 dias" },
];

const FILTROS_INICIAIS = {
  dias: 30,
  fornecedor_id: "",
  parado_dias: 0,
  somente_urgentes: false,
  somente_com_furo: false,
  somente_sem_pedido: false,
  busca: "",
};

const moeda = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// data_lista chega como "AAAA-MM-DD": new Date() leria isso como meia-noite UTC
// e, no fuso de Brasilia, mostraria o dia anterior. Datas com hora seguem no new Date().
const data = (v) => {
  if (!v) return "-";
  const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return soData
    ? `${soData[3]}/${soData[2]}/${soData[1]}`
    : new Date(v).toLocaleDateString("pt-BR");
};

export default function ListasComprasKanban() {
  const navigate = useNavigate();
  const toast = useToast();

  const [cols, setCols] = useState(VAZIO);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fornecedores, setFornecedores] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [buscaInput, setBuscaInput] = useState("");
  const [retirada, setRetirada] = useState(null); // lista sendo marcada/editada
  const [detalheId, setDetalheId] = useState(null); // lista aberta no modal
  const [desfazendo, setDesfazendo] = useState(null); // lista com retirada a desfazer
  const [processando, setProcessando] = useState(false);
  // Coleta Lalamove (#2307): {compra} para pedir, {ordemId} para abrir a corrida.
  const [coleta, setColeta] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    const params = { dias: filtros.dias };
    if (filtros.fornecedor_id) params.fornecedor_id = filtros.fornecedor_id;
    if (filtros.parado_dias) params.parado_dias = filtros.parado_dias;
    if (filtros.somente_urgentes) params.somente_urgentes = 1;
    if (filtros.somente_com_furo) params.somente_com_furo = 1;
    if (filtros.somente_sem_pedido) params.somente_sem_pedido = 1;
    if (filtros.busca) params.busca = filtros.busca;

    const r = await getListasComprasKanban(params);
    if (r) {
      setCols({ ...VAZIO, ...(r.colunas || {}) });
      setResumo(r.resumo || null);
    } else {
      toast({ title: "Erro ao carregar o quadro", status: "error", isClosable: true });
    }
    setLoading(false);
  }, [filtros, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    (async () => {
      const cfg = await getConfiguracoesListaCompras();
      const lista = cfg?.fornecedores || [];
      setFornecedores([...lista].sort((a, b) => (a.nome || "").localeCompare(b.nome || "")));
    })();
  }, []);

  const setFiltro = (campo, valor) => setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const limpar = () => { setBuscaInput(""); setFiltros(FILTROS_INICIAIS); };

  // Arrastar só serve para levar uma lista aprovada até "Retirado" — o modal
  // pede o link da entrega antes de gravar, então nada muda na tela até salvar.
  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination || destination.droppableId !== "retirado") return;
    if (source.droppableId !== "aprovado") return;
    const lista = (cols.aprovado || []).find((l) => String(l.id) === draggableId);
    if (lista) setRetirada(lista);
  };

  const desfazer = async () => {
    const lista = desfazendo;
    if (!lista) return;
    setProcessando(true);
    const r = await desfazerListaRetirada(lista.id);
    setProcessando(false);
    setDesfazendo(null);
    if (r?.lista) {
      toast({ title: "Retirada desfeita", description: `A lista #${lista.id} voltou para Aprovado.`, status: "success", isClosable: true });
      carregar();
    } else {
      toast({ title: r?.message || "Não foi possível desfazer", status: "error", isClosable: true });
    }
  };

  // Aprovar/finalizar dentro do modal muda a coluna da lista — recarrega ao fechar.
  const fecharDetalhe = () => { setDetalheId(null); carregar(); };

  const totalCards = useMemo(
    () => COLUNAS.reduce((acc, c) => acc + (cols[c.key]?.length || 0), 0),
    [cols]
  );

  return (
    // A sidebar e fixa (64px recolhida), entao a pagina reserva a faixa dela —
    // mesmo padrao de CoberturaFuros/PendenciasVendas. No mobile a sidebar sai
    // de cena e sobra o botao flutuante do menu, que vai ate ~110px do topo.
    <Box
      ml={{ base: 0, md: "64px" }}
      pt={{ base: "60px", md: 6 }}
      pb={10}
      px={{ base: 3, md: 6 }}
      minH="100vh"
      bg="#f0f0f0"
    >
      <Box>
        <HStack justifyContent="space-between" mb={5} flexWrap="wrap" gap={2}>
          <HStack spacing={2}>
            <IconButton
              icon={<FiArrowLeft />}
              onClick={() => navigate("/listas-compras/dashboard")}
              variant="ghost"
              aria-label="Voltar"
            />
            <Heading size="lg" color={TEAL}>Kanban de Compras</Heading>
          </HStack>
          <HStack spacing={2} flexWrap="wrap">
            <Button size="sm" variant="ghost" leftIcon={<FiRefreshCw />} onClick={carregar}>
              Atualizar
            </Button>
            <Button size="sm" variant="outline" leftIcon={<FiList />} onClick={() => navigate("/listas-compras")}>
              Ver em tabela
            </Button>
            <Button size="sm" colorScheme="green" bg={TEAL} onClick={() => navigate("/listas-compras/nova")}>
              + Nova Lista
            </Button>
          </HStack>
        </HStack>

        {/* Filtros */}
        <Card mb={4} boxShadow="sm">
          <CardBody py={4}>
            <Flex gap={3} flexWrap="wrap" align="flex-end">
              <Box minW="130px">
                <Text fontSize="xs" fontWeight="bold" mb={1}>Período</Text>
                <Select size="sm" value={filtros.dias} onChange={(e) => setFiltro("dias", Number(e.target.value))}>
                  {PERIODOS.map((d) => <option key={d} value={d}>Últimos {d} dias</option>)}
                </Select>
              </Box>

              <Box minW="190px" flex="1">
                <Text fontSize="xs" fontWeight="bold" mb={1}>Fornecedor</Text>
                <Select size="sm" placeholder="Todos" value={filtros.fornecedor_id}
                  onChange={(e) => setFiltro("fornecedor_id", e.target.value)}>
                  {fornecedores.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </Select>
              </Box>

              <Box minW="150px">
                <Text fontSize="xs" fontWeight="bold" mb={1}>Parada há</Text>
                <Select size="sm" value={filtros.parado_dias}
                  onChange={(e) => setFiltro("parado_dias", Number(e.target.value))}>
                  {PARADO_OPCOES.map((o) => <option key={o.valor} value={o.valor}>{o.label}</option>)}
                </Select>
              </Box>

              <Box minW="220px" flex="1">
                <Text fontSize="xs" fontWeight="bold" mb={1}>Buscar</Text>
                <HStack spacing={1}>
                  <Input
                    size="sm" placeholder="Nº do pedido, fornecedor ou #id"
                    value={buscaInput}
                    onChange={(e) => setBuscaInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") setFiltro("busca", buscaInput.trim()); }}
                  />
                  <IconButton size="sm" aria-label="Buscar" icon={<FiSearch />}
                    onClick={() => setFiltro("busca", buscaInput.trim())} />
                </HStack>
              </Box>
            </Flex>

            <HStack mt={3} spacing={5} flexWrap="wrap">
              <Checkbox size="sm" colorScheme="orange" isChecked={filtros.somente_urgentes}
                onChange={(e) => setFiltro("somente_urgentes", e.target.checked)}>
                Só com item urgente
              </Checkbox>
              <Checkbox size="sm" colorScheme="orange" isChecked={filtros.somente_com_furo}
                onChange={(e) => setFiltro("somente_com_furo", e.target.checked)}>
                Só com furo de estoque
              </Checkbox>
              <Checkbox size="sm" colorScheme="orange" isChecked={filtros.somente_sem_pedido}
                onChange={(e) => setFiltro("somente_sem_pedido", e.target.checked)}>
                Só sem nº do pedido
              </Checkbox>
              <Button size="xs" variant="ghost" onClick={limpar}>Limpar filtros</Button>
            </HStack>
          </CardBody>
        </Card>

        {resumo && (
          <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3} mb={4}>
            {COLUNAS.map((c) => (
              <CardStat key={c.key} label={c.label} valor={resumo[c.key] ?? 0} cor={c.cor} />
            ))}
            <CardStat label="Valor aprovado" valor={moeda(resumo.valor_aprovado)} cor="teal" />
          </SimpleGrid>
        )}

        {resumo?.truncado && (
          <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
            <AlertIcon />
            O quadro mostra apenas as {resumo.limite} listas mais recentes do período. Reduza o período ou filtre por fornecedor para ver as mais antigas.
          </Alert>
        )}

        <Text fontSize="sm" color="gray.500" mb={2}>
          Arraste um card de <b>Aprovado</b> para <b>Retirado</b> quando a mercadoria sair do fornecedor.
          As outras colunas são definidas pelo próprio fluxo (resposta do fornecedor e aprovação).
          {/* Corte fixo no back (#2274): o período só estreita, nunca alcança o que ficou pra trás. */}
          {resumo?.data_minima && (
            <> O quadro não mostra listas anteriores a <b>{resumo.data_minima.split("-").reverse().join("/")}</b>;
            as antigas continuam na <b>visão em tabela</b>.</>
          )}
        </Text>

        {loading ? (
          <VStack py={16}><Spinner size="xl" color={ACCENT} thickness="4px" /></VStack>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3} alignItems="start">
              {COLUNAS.map((col) => {
                const lista = cols[col.key] || [];
                const podeSoltar = col.key === "retirado";
                return (
                  <Droppable droppableId={col.key} key={col.key} isDropDisabled={!podeSoltar}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        bg={snapshot.isDraggingOver ? "purple.50" : "gray.50"}
                        borderWidth="1px"
                        borderColor={snapshot.isDraggingOver ? "purple.200" : "gray.100"}
                        borderRadius="xl"
                        p={2}
                        minH="140px"
                        transition="background 0.12s"
                      >
                        <HStack mb={1} px={1} justify="space-between">
                          <HStack spacing={2}>
                            <Box w="8px" h="8px" borderRadius="full" bg={`${col.cor}.400`} />
                            <Text fontWeight="bold" fontSize="sm">{col.label}</Text>
                          </HStack>
                          <Badge colorScheme={col.cor} borderRadius="full">{lista.length}</Badge>
                        </HStack>
                        <Text fontSize="11px" color="gray.400" px={1} mb={2}>{col.ajuda}</Text>

                        {/* So no layout de 4 colunas a coluna rola sozinha; empilhada,
                            quem rola e a pagina. */}
                        <VStack
                          align="stretch"
                          spacing={2}
                          maxH={{ base: "none", xl: "calc(100dvh - 430px)" }}
                          overflowY={{ base: "visible", xl: "auto" }}
                        >
                          {lista.map((l, idx) => (
                            <Draggable
                              draggableId={String(l.id)}
                              index={idx}
                              key={l.id}
                              isDragDisabled={col.key !== "aprovado"}
                            >
                              {(prov, snap) => (
                                <Box
                                  ref={prov.innerRef}
                                  {...prov.draggableProps}
                                  {...prov.dragHandleProps}
                                  bg="white"
                                  borderWidth="1px"
                                  borderColor="gray.100"
                                  borderLeftWidth="4px"
                                  borderLeftColor={`${col.cor}.400`}
                                  borderRadius="lg"
                                  p={3}
                                  boxShadow={snap.isDragging ? "lg" : "xs"}
                                  _hover={{ boxShadow: "sm" }}
                                >
                                  <CardLista
                                    lista={l}
                                    coluna={col.key}
                                    onAbrir={() => setDetalheId(l.id)}
                                    onRetirar={() => setRetirada(l)}
                                    onDesfazer={() => setDesfazendo(l)}
                                    onLalamove={() => setColeta({ compra: { tipo: "lista", id: l.id } })}
                                    onAbrirCorrida={(ordemId) => setColeta({ ordemId })}
                                  />
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {lista.length === 0 && (
                            <Text fontSize="xs" color="gray.300" textAlign="center" py={5}>—</Text>
                          )}
                        </VStack>
                      </Box>
                    )}
                  </Droppable>
                );
              })}
            </SimpleGrid>
          </DragDropContext>
        )}

        {!loading && totalCards === 0 && (
          <Alert status="info" borderRadius="md" mt={4}>
            <AlertIcon />
            Nenhuma lista no período com os filtros aplicados.
          </Alert>
        )}
      </Box>

      <ModalRetirada
        lista={retirada}
        onClose={() => setRetirada(null)}
        onSalvo={() => { setRetirada(null); carregar(); }}
      />

      <ModalColetaLalamove
        isOpen={!!coleta}
        onClose={() => setColeta(null)}
        compra={coleta?.compra || null}
        ordemId={coleta?.ordemId || null}
        onMudou={carregar}
      />

      <ConfirmDialog
        isOpen={!!desfazendo}
        onClose={() => setDesfazendo(null)}
        onConfirm={desfazer}
        isLoading={processando}
        title={`Desfazer a retirada da lista #${desfazendo?.id}?`}
        body="A lista volta para Aprovado e o link e a observação da entrega são apagados."
        confirmText="Desfazer retirada"
        confirmColor="red"
        cancelText="Voltar"
      />

      <Modal isOpen={!!detalheId} onClose={fecharDetalhe} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="92vh" my={4} bg="#f0f0f0">
          <ModalHeader fontSize="md" bg="white" borderBottomWidth="1px" pr={12}>
            <HStack spacing={3} flexWrap="wrap">
              <Text>Lista #{detalheId}</Text>
              <Button
                size="xs" variant="outline" leftIcon={<FiExternalLink />}
                as="a" href={linkDetalhes(detalheId)} target="_blank" rel="noopener noreferrer"
              >
                Abrir em nova aba
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody px={{ base: 2, md: 4 }} py={4}>
            {detalheId && (
              <ListaCompraDetalhes listaId={detalheId} emModal onFechar={fecharDetalhe} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function CardStat({ label, valor, cor }) {
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="lg" px={3} py={2}>
      <Text fontSize="11px" color="gray.500" textTransform="uppercase">{label}</Text>
      <Text fontSize="lg" fontWeight="bold" color={`${cor}.600`}>{valor}</Text>
    </Box>
  );
}

function CardLista({ lista, coluna, onAbrir, onRetirar, onDesfazer, onLalamove, onAbrirCorrida }) {
  const completo = lista.quantidade_itens > 0
    && lista.quantidade_itens_respondidos >= lista.quantidade_itens;
  // Corrida em andamento: o botao vira o selo (abre a corrida), para nao pedir duas.
  const corridaAtiva = !!lista.lalamove?.ativa;

  return (
    <>
      <HStack justify="space-between" align="start" spacing={2} mb={1}>
        <Text
          fontSize="sm" fontWeight="bold" color={TEAL} noOfLines={2} flex="1" minW={0}
          cursor="pointer" _hover={{ textDecoration: "underline" }} onClick={onAbrir}
        >
          {lista.fornecedor}
        </Text>
        <Text fontSize="11px" color="gray.400" whiteSpace="nowrap">#{lista.id}</Text>
      </HStack>

      <HStack fontSize="xs" color="gray.500" spacing={3} flexWrap="wrap" mb={2}>
        <Text>{data(lista.data_lista)}</Text>
        <Text>
          {lista.quantidade_itens_respondidos || 0}/{lista.quantidade_itens} itens
          {!completo && coluna === "pendente" ? " respondidos" : ""}
        </Text>
        {lista.valor_aprovado > 0 && (
          <Text fontWeight="semibold" color="gray.600">{moeda(lista.valor_aprovado)}</Text>
        )}
      </HStack>

      <HStack spacing={1} flexWrap="wrap" mb={2}>
        {lista.itens_urgentes > 0 && (
          <Badge colorScheme="red" variant="subtle">{lista.itens_urgentes} urgente(s)</Badge>
        )}
        {lista.itens_furo > 0 && (
          <Badge colorScheme="orange" variant="subtle">{lista.itens_furo} furo(s)</Badge>
        )}
        {lista.finalizada_automaticamente && (
          <Tooltip label="Aprovada e finalizada automaticamente pelo sistema" hasArrow>
            <Badge colorScheme="cyan" variant="solid">🤖 Auto</Badge>
          </Tooltip>
        )}
        {lista.status_envio === "erro" && <Badge colorScheme="red">Erro no envio</Badge>}
        {lista.status_envio === "pendente" && <Badge colorScheme="gray">Não enviada</Badge>}
        {lista.dias_parado >= 2 && (
          <Badge colorScheme={lista.dias_parado >= 5 ? "red" : "gray"} variant="outline">
            parada há {lista.dias_parado}d
          </Badge>
        )}
      </HStack>

      {/* Código do pedido: o fornecedor informa na página pública dele. */}
      {(coluna === "aprovado" || coluna === "retirado") && (
        lista.numero_pedido_formalizado ? (
          <Box bg="green.50" borderRadius="md" px={2} py={1} mb={2}>
            <Text fontSize="10px" color="green.700" textTransform="uppercase">Pedido do fornecedor</Text>
            {/* Fornecedor às vezes informa vários números numa string só. */}
            <Tooltip label={lista.numero_pedido_formalizado} hasArrow openDelay={400}>
              <Text fontSize="sm" fontWeight="bold" color="green.800" noOfLines={2} wordBreak="break-word">
                {lista.numero_pedido_formalizado}
              </Text>
            </Tooltip>
          </Box>
        ) : (
          <Text fontSize="11px" color="gray.400" mb={2}>Aguardando o nº do pedido do fornecedor</Text>
        )
      )}

      {(coluna === "aprovado" || coluna === "retirado") && lista.lalamove && (
        <SeloCorrida corrida={lista.lalamove} onAbrir={() => onAbrirCorrida(lista.lalamove.ordem_id)} />
      )}

      {coluna === "retirado" && (
        <Box bg="purple.50" borderRadius="md" px={2} py={1} mb={2}>
          <Text fontSize="10px" color="purple.700" textTransform="uppercase">Entrega</Text>
          {lista.rastreio_link ? (
            <Link href={lista.rastreio_link} isExternal fontSize="xs" color="purple.700" fontWeight="semibold" wordBreak="break-all">
              Acompanhar <FiExternalLink style={{ display: "inline" }} />
            </Link>
          ) : (
            <Text fontSize="xs" color="purple.600">Sem link de entrega</Text>
          )}
          {lista.retirada_observacoes && (
            <Text fontSize="11px" color="gray.600" mt={1} noOfLines={2}>{lista.retirada_observacoes}</Text>
          )}
          <Text fontSize="10px" color="gray.500" mt={1}>
            {data(lista.retirado_em)}{lista.retirado_por ? ` · ${lista.retirado_por}` : ""}
          </Text>
        </Box>
      )}

      <HStack spacing={1} justify="flex-end" flexWrap="wrap">
        {coluna === "aprovado" && !corridaAtiva && (
          <Tooltip label="Pedir corrida Lalamove para buscar no fornecedor" hasArrow>
            <Button size="xs" colorScheme="teal" variant="solid" onClick={onLalamove}>
              Lalamove
            </Button>
          </Tooltip>
        )}
        {coluna === "aprovado" && (
          <Button size="xs" colorScheme="purple" variant="outline" leftIcon={<FiTruck />} onClick={onRetirar}>
            Marcar retirada
          </Button>
        )}
        {coluna === "retirado" && (
          <>
            <Button size="xs" variant="ghost" onClick={onRetirar}>Editar entrega</Button>
            <Tooltip label="Desfazer retirada" hasArrow>
              <IconButton size="xs" variant="ghost" aria-label="Desfazer retirada"
                icon={<FiRotateCcw />} onClick={onDesfazer} />
            </Tooltip>
          </>
        )}
        <Button
          size="xs" variant="ghost" colorScheme="orange"
          as="a" href={linkDetalhes(lista.id)}
          onClick={(e) => {
            // Com Ctrl/Cmd/Shift deixa o navegador abrir em outra aba.
            if (e.ctrlKey || e.metaKey || e.shiftKey) return;
            e.preventDefault();
            onAbrir();
          }}
        >
          Abrir
        </Button>
      </HStack>
    </>
  );
}

function ModalRetirada({ lista, onClose, onSalvo }) {
  const toast = useToast();
  const [link, setLink] = useState("");
  const [obs, setObs] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setLink(lista?.rastreio_link || "");
    setObs(lista?.retirada_observacoes || "");
  }, [lista]);

  const salvar = async () => {
    setSalvando(true);
    const r = await marcarListaRetirada(lista.id, {
      rastreio_link: link.trim() || null,
      retirada_observacoes: obs.trim() || null,
    });
    setSalvando(false);
    if (r?.lista) {
      toast({ title: r.message || "Retirada registrada", status: "success", isClosable: true });
      onSalvo();
    } else {
      toast({ title: r?.message || "Não foi possível registrar a retirada", status: "error", isClosable: true });
    }
  };

  return (
    <Modal isOpen={!!lista} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          Retirada da lista #{lista?.id}
          <Text fontSize="sm" color="gray.500" fontWeight="normal">{lista?.fornecedor}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <FormControl>
              <FormLabel fontSize="sm">Link da entrega (Lalamove)</FormLabel>
              <Input
                size="sm" placeholder="https://..." value={link}
                onChange={(e) => setLink(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") salvar(); }}
              />
              <Text fontSize="11px" color="gray.500" mt={1}>
                Opcional. Cole o link de acompanhamento do Lalamove ou de outro entregador.
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Observações da retirada</FormLabel>
              <Textarea size="sm" rows={3} placeholder="Ex.: retirado pelo motoboy às 14h, faltou 1 item"
                value={obs} onChange={(e) => setObs(e.target.value)} />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancelar</Button>
          <Button size="sm" colorScheme="purple" onClick={salvar} isLoading={salvando}>
            {lista?.status_resposta === "retirado" ? "Salvar" : "Marcar como retirada"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
